import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

export type TemplateKind = "mail" | "auto";
export type MediaPosition = "top" | "bottom";

export interface MessageEntity {
  type:
    | "bold"
    | "italic"
    | "underline"
    | "strikethrough"
    | "code"
    | "pre"
    | "spoiler"
    | "text_link"
    | "mention"
    | "text_mention"
    | "custom_emoji"
    | "blockquote";
  offset: number;
  length: number;
  url?: string | null;
  user_id?: number | null;
  language?: string | null;
  custom_emoji_id?: string | null;
  custom_emoji_is_free?: boolean | null;
}

export interface Template {
  id: number;
  kind: TemplateKind;
  name: string;
  text: string;
  entities: MessageEntity[];
  media_path: string | null;
  media_position: MediaPosition;
  created_at: string;
}

export interface TemplateUpsert {
  kind: TemplateKind;
  name: string;
  text: string;
  entities: MessageEntity[];
  media_path: string | null;
  media_position: MediaPosition;
}

export function useTemplates() {
  return useQuery<Template[]>({
    queryKey: ["templates"],
    queryFn: () => api.get<Template[]>("/templates"),
    staleTime: 30_000,
  });
}

export function useTemplate(id: number | null) {
  return useQuery<Template>({
    queryKey: ["templates", id],
    queryFn: () => api.get<Template>(`/templates/${id}`),
    enabled: id !== null,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TemplateUpsert) => api.post<Template>("/templates", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }),
  });
}

export function useUpdateTemplate(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<TemplateUpsert>) => api.patch<Template>(`/templates/${id}`, body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["templates"] });
      qc.setQueryData(["templates", id], data);
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }),
  });
}

export function useDuplicateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<Template>(`/templates/${id}/duplicate`),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["templates"] });
      qc.setQueryData(["templates", data.id], data);
    },
  });
}

export function useSpintaxPreview() {
  return useMutation({
    mutationFn: (params: { text: string; samples?: number }) =>
      api.post<{ samples: string[] }>("/templates/spintax/preview", params),
  });
}

export async function uploadTemplateMedia(file: File): Promise<{ path: string; size: number }> {
  const fd = new FormData();
  fd.append("file", file);
  return api.post<{ path: string; size: number }>("/templates/media", fd, {
    isFormData: true,
  });
}
